import {
	columnVisibilityFeature,
	createColumnHelper,
	metaHelper,
	rowSortingFeature,
	tableFeatures,
} from '@tanstack/react-table';
import { Move3d, RotateCcw, RotateCw, ScrollText } from 'lucide-react';
import type { ReactNode } from 'react';

import type { DataUpload, Tileset } from '@/common/data-upload';
import type { WithDeletedBy } from '@/common/soft-delete';
import { DataTableColumnHeader } from '@/components/data-table/data-table-column-header';
import type { DataTableColumnMeta } from '@/components/data-table/data-table-features';
import type { SortFieldMap } from '@/components/data-table/sort-param';
import { DeletedOnIcon } from '@/components/soft-delete/DeletedInfo';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import type { BulkSelection } from '@/hooks/use-bulk-selection';
import { t } from '@/i18n';
import { cn, formatDateTime } from '@/lib/utils';
import { getDataUploadTypeIconConfig, isTilesetDataUpload } from '@/lib/utils/data-upload';

export interface DataUploadTableMeta {
	isReadOnly: boolean;
	isReprocessPending: boolean;
	onShowLog: (dataUpload: DataUpload) => void;
	onReprocess: (dataUpload: DataUpload) => void;
	onSetLocation: (dataUpload: DataUpload) => void;
	/** Bulk-delete selection; the `select` column is hidden for users who can't delete. */
	selection: BulkSelection;
	/** Deleted-items view (GH #331): the actions column offers Restore only */
	onRestore?: (dataUpload: DataUpload) => void;
}

// Sorting is server-side (`manualSorting`), so no sorted row model or sort fns are registered.
export const dataUploadTableFeatures = tableFeatures({
	columnVisibilityFeature,
	rowSortingFeature,
	tableMeta: metaHelper<DataUploadTableMeta>(),
	columnMeta: metaHelper<DataTableColumnMeta>(),
});

// Column id → backend `sort` field. Must match DATA_UPLOAD_SORT_FIELDS in the backend DTO.
export const DATA_UPLOAD_SORT_FIELDS: SortFieldMap = {
	name: 'name',
	subAsset: 'subAsset.name',
	createdAt: 'createdAt',
	status: 'tileset.status',
};

// Reprocess is offered for tilesets that are finished (ready) or failed (error);
// the tile server rejects anything still queued/processing.
export function canReprocessTileset(dataUpload: DataUpload): boolean {
	const status = dataUpload.tileset?.status;
	return isTilesetDataUpload(dataUpload) && status != null && status !== 'queued' && status !== 'processing';
}

const tilesetStatusClasses: Record<string, string> = {
	ready: 'bg-emerald-100 text-emerald-700',
	processing: 'bg-amber-100 text-amber-700',
	queued: 'bg-slate-100 text-slate-600',
	error: 'bg-red-100 text-red-700',
};

function TilesetStatusBadge({ tileset }: { tileset?: Tileset | null }) {
	const status = tileset?.status;
	if (!status) return null;
	// Tile server feedback: pipeline stage while processing, failure message on error.
	const detail = status === 'error' ? tileset?.errorMessage : tileset?.stage;
	const label =
		status === 'ready'
			? t('files.tileset.ready')
			: status === 'processing'
				? t('files.tileset.statusProcessing')
				: status === 'queued'
					? t('files.tileset.statusQueued')
					: status === 'error'
						? t('files.tileset.statusError')
						: status;
	const badge = (
		<span
			className={cn(
				'shrink-0 rounded px-1.5 py-0.5 text-[10px] font-medium',
				tilesetStatusClasses[status] || 'bg-slate-100 text-slate-600',
			)}
		>
			{label}
		</span>
	);
	if (!detail) return badge;
	return (
		<Tooltip>
			<TooltipTrigger asChild>{badge}</TooltipTrigger>
			<TooltipContent className="max-w-xs whitespace-pre-wrap break-words">{detail}</TooltipContent>
		</Tooltip>
	);
}

function RowAction({
	label,
	icon,
	onClick,
	disabled,
}: {
	label: string;
	icon: ReactNode;
	onClick: () => void;
	disabled?: boolean;
}) {
	return (
		<Tooltip>
			<TooltipTrigger asChild>
				<Button
					variant="outline"
					size="icon"
					className="size-6 shrink-0 cursor-pointer"
					disabled={disabled}
					onClick={(e) => {
						e.stopPropagation();
						onClick();
					}}
				>
					{icon}
					<span className="sr-only">{label}</span>
				</Button>
			</TooltipTrigger>
			<TooltipContent>{label}</TooltipContent>
		</Tooltip>
	);
}

const columnHelper = createColumnHelper<typeof dataUploadTableFeatures, DataUpload>();

export const dataUploadColumns = columnHelper.columns([
	columnHelper.display({
		id: 'select',
		header: ({ table }) => {
			const selection = table.options.meta?.selection;
			if (!selection) return null;
			return (
				<Checkbox
					checked={selection.allSelected ? true : selection.someSelected ? 'indeterminate' : false}
					onCheckedChange={selection.toggleAll}
					aria-label={t('files.list.selectAllAria')}
				/>
			);
		},
		cell: ({ row, table }) => {
			const selection = table.options.meta?.selection;
			if (!selection) return null;
			const id = row.original.id as string;
			return (
				<Checkbox
					checked={selection.isSelected(id)}
					onCheckedChange={() => selection.toggle(id)}
					aria-label={t('files.list.selectRowAria', { name: row.original.name ?? t('common.labels.file') })}
				/>
			);
		},
		meta: { stopRowClick: true, headerClassName: 'w-10' },
	}),
	columnHelper.accessor((row) => row.name ?? '', {
		id: 'name',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('files.upload.fileNameLabel')} />,
		cell: ({ row }) => {
			const iconConfig = getDataUploadTypeIconConfig(row.original);
			const { deletedAt, deletedBy } = row.original as WithDeletedBy<DataUpload>;
			return (
				<div className="min-w-0">
					<div className="flex items-center gap-2 min-w-0">
						<iconConfig.icon className={cn('h-4 w-4 shrink-0', iconConfig.color)} />
						<span className="font-medium truncate">{row.original.name}</span>
						<DeletedOnIcon deletedAt={deletedAt} deletedBy={deletedBy} />
					</div>
				</div>
			);
		},
		// No width: the name takes what the other columns leave.
		meta: { className: 'max-w-0' },
	}),
	// The table is table-fixed, so the widths in the header decide: the status and the actions get
	// fixed widths, the name takes the rest. Optional columns drop out by the table's own width
	// (DataTable is a container), not the viewport's, as the panel next to the 3D viewer is narrow
	// on a wide screen: below 48rem the date, below 36rem the sub-asset (the asset filter tells it).
	// Shows the sub-asset, falling back to the asset for asset-level uploads. Sorting is by sub-asset
	// name, so asset-level uploads (no sub-asset) sort last ascending and first descending.
	columnHelper.accessor((row) => row.subAsset?.name ?? row.asset?.name ?? '', {
		id: 'subAsset',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('files.list.subAssetColumn')} />,
		meta: {
			className: 'max-w-0 truncate hidden @xl:table-cell',
			headerClassName: 'hidden @xl:table-cell @xl:w-1/4',
		},
	}),
	columnHelper.accessor((row) => row.createdAt ?? '', {
		id: 'createdAt',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('files.list.uploadedAtColumn')} />,
		cell: ({ getValue }) => formatDateTime(getValue()),
		meta: {
			className: 'max-w-0 truncate hidden @3xl:table-cell',
			headerClassName: 'hidden @3xl:table-cell @3xl:w-1/5',
		},
	}),
	columnHelper.accessor((row) => row.tileset?.status ?? '', {
		id: 'status',
		header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.labels.status')} />,
		cell: ({ row }) =>
			isTilesetDataUpload(row.original) ? <TilesetStatusBadge tileset={row.original.tileset} /> : null,
		meta: { className: 'whitespace-nowrap', headerClassName: 'w-28' },
	}),
	columnHelper.display({
		id: 'actions',
		header: () => <span className="sr-only">{t('common.labels.actions')}</span>,
		cell: ({ row, table }) => {
			const dataUpload = row.original;
			const meta = table.options.meta;
			if (!meta) return null;
			const status = dataUpload.tileset?.status;
			const isTileset = isTilesetDataUpload(dataUpload);
			if (meta.onRestore) {
				return (
					<div className="flex justify-end">
						<Button variant="outline" size="sm" onClick={() => meta.onRestore?.(dataUpload)}>
							<RotateCcw />
							{t('common.actions.restore')}
						</Button>
					</div>
				);
			}
			return (
				<div className="flex items-center justify-end gap-1">
					{/* Log is for tracking/debugging a conversion; once it's ready there is nothing to inspect. */}
					{isTileset && status != null && status !== 'ready' && (
						<RowAction
							label={t('files.list.conversionLog')}
							icon={<ScrollText className="size-3.5" />}
							onClick={() => meta.onShowLog(dataUpload)}
						/>
					)}
					{!meta.isReadOnly && canReprocessTileset(dataUpload) && (
						<RowAction
							label={t('files.tileset.reprocessAction')}
							icon={<RotateCw className="size-3.5" />}
							disabled={meta.isReprocessPending}
							onClick={() => meta.onReprocess(dataUpload)}
						/>
					)}
					{!meta.isReadOnly && isTileset && status === 'ready' && (
						<RowAction
							label={t('files.location.title')}
							icon={<Move3d className="size-3.5" />}
							onClick={() => meta.onSetLocation(dataUpload)}
						/>
					)}
				</div>
			);
		},
		// Room for three buttons. Without a width the column only got what the others left, which
		// was nothing in a narrow panel, and the buttons spilled out of the table.
		// Its buttons act on the row (log, reprocess, location, restore) and must not also open the preview
		meta: { className: 'text-right', headerClassName: 'w-28 text-right', stopRowClick: true },
	}),
]);
